import Microgame from '../engine/Microgame.js';

export default class KeepUpGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Jongle !`, en: `Keep Up!`, es: `¡Malabares!` },
            controls: { fr: `Appuie sous la balle pour la frapper vers le haut`, en: `Tap under the ball to hit it up`, es: `Toca debajo de la bola para golpearla hacia arriba` },
            objective: { fr: `Ne laisse pas la balle toucher le sol`, en: `Don't let the ball hit the floor`, es: `No dejes que la bola toque el suelo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>`,
            actions: [
                { type: 'wait', duration: 300 },
                { type: 'tap', x: 50, y: 28, duration: 160 },
                { type: 'wait', duration: 700 },
                { type: 'tap', x: 50, y: 30, duration: 160 },
                { type: 'wait', duration: 700 },
                { type: 'tap', x: 50, y: 32, duration: 160 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 3400
        };
    }
    constructor(engine) {
        super(engine);
        this.ball = null;
        
        const speedMult = engine.speedMultiplier || 1.0;
        
        // Random horizontal start (20% to 80%)
        this.x = this.isPlayback ? 50 : (20 + Math.random() * 60); 
        
        // Start high at low difficulty, low at high difficulty. Capt at 70% max.
        let targetY = 20 + (speedMult - 1) * 15;
        this.y = this.isPlayback ? 20 : Math.min(targetY, 70); 

        this.vx = 0; // %/s
        this.vy = 0; // %/s
        this.gravity = 250; // %/s^2
        this.forceY = -120; // %/s (Vertical bounce power)
        this.lost = false;
        this.taps = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "TAP THE BALL!";

        this.ball = document.createElement('div');
        this.ball.style.position = 'absolute';
        this.ball.style.width = '80px'; // Slightly larger for better touch target
        this.ball.style.height = '80px';
        this.ball.style.background = this.secondaryColor;
        this.ball.style.borderRadius = '50%';
        this.ball.style.left = '50%';
        this.ball.style.top = '50%';
        this.ball.style.transform = 'translate(-50%, -50%)';
        this.ball.style.boxShadow = `0 0 20px ${this.secondaryColor}, inset 0 0 20px rgba(0,0,0,0.2)`;
        this.ball.style.touchAction = 'none';
        this.ball.style.cursor = 'pointer';
        this.ball.style.userSelect = 'none';
        this.ball.style.webkitUserSelect = 'none'; // Safari
        this.ball.style.webkitTapHighlightColor = 'transparent'; // Mobile
        this.ball.style.outline = 'none';
        this.container.appendChild(this.ball);

        // Input - Listen on the container but calculate distance manually
        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);
    }

    onTimeout() {
        return true; // Win if timer finishes
    }

    onTap(e) {
        if (this.lost || !this.active) return;

        const rect = this.container.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        // Ball position in pixels
        const ballPxX = (this.x / 100) * rect.width;
        const ballPxY = (this.y / 100) * rect.height;

        // Distance check
        const dx = clickX - ballPxX;
        const dy = clickY - ballPxY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const radius = 60; // Visual is 40px radius (80px width), but hit area generous

        if (dist < radius) {
            // HIT!

            // Vertical impulse
            this.vy = this.forceY;

            // Horizontal impulse based on offset
            // If dx > 0 (clicked right of center), vx should be negative (go left)
            // If dx < 0 (clicked left of center), vx should be positive (go right)
            // Normalize dx relative to radius roughly (-1 to 1) -> multiply by max speed
            this.vx = -(dx / 40) * 100; // Adjust 100 to change horizontal sensitivity

            // Limit vx
            this.vx = Math.max(-150, Math.min(150, this.vx));


            // Infinite Mode & Score
            if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite_no_timer') {
                this.taps++;
                this.engine.challengesCompleted = this.taps;
                this.engine.score = this.taps;
                this.engine.updateHUD();

                if (this.taps % 5 === 0) {
                    this.gravity *= 1.05;
                }
            }

            // Visual feedback
            this.ball.animate([
                { transform: 'translate(-50%, -50%) scale(0.9)' },
                { transform: 'translate(-50%, -50%) scale(1.1)' },
                { transform: 'translate(-50%, -50%) scale(1)' }
            ], { duration: 150 });

            // Optional: Add particle or sound here
        }
    }

    update(dt) {
        if (this.lost) return;

        const dtSec = dt / 1000;

        // Physics
        this.vy += this.gravity * dtSec;
        this.x += this.vx * dtSec;
        this.y += this.vy * dtSec;

        // Wall Collisions (X)
        // Ball width is 80px approx ~ 20% of screen depending on resolution?
        // Let's approximate boundary: 10% to 90% (since transform is center based)
        // Better: strict pixel check or safe margin. 10% margin is safe for responsiveness.
        if (this.x < 10) {
            this.x = 10;
            this.vx = -this.vx * 0.8; // Bounce with dampening
        } else if (this.x > 90) {
            this.x = 90;
            this.vx = -this.vx * 0.8;
        }

        // Ceiling
        if (this.y < 10) {
            this.y = 10;
            this.vy = 0; // or bounce positive? 
        }

        // Floor Collision (Loss)
        if (this.y > 100) {
            this.lost = true;
            this.ball.style.background = '#ff4444';
            this.lose();
        }

        // Apply visual
        this.ball.style.left = `${this.x}%`;
        this.ball.style.top = `${this.y}%`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
